/**
 * Earth structural integrity meter (stress bar).
 *
 * Replaces the previous 3-heart UI. Theme: Earth's structural integrity
 * out of 100. Big-tier meteors that settle past the atmosphere deal more
 * stress; integrity drains accordingly. Bar color shifts green → amber
 * → red as percentage drops, providing at-a-glance danger awareness.
 */

import type { AtmosphereTracker } from "../game/atmosphere.ts";
import { t } from "../i18n/index.ts";

export interface IntegrityMeter {
  element: HTMLDivElement;
  /** Trigger a brief shake on damage. */
  punchLoss(): void;
  detach(): void;
}

const HEALTHY_HEX = "#7ee68f"; // green
const WARNING_HEX = "#f5c453"; // amber
const CRITICAL_HEX = "#ff5b5b"; // red

function lerpHex(from: string, to: string, t: number): string {
  const fr = parseInt(from.slice(1, 3), 16);
  const fg = parseInt(from.slice(3, 5), 16);
  const fb = parseInt(from.slice(5, 7), 16);
  const tr = parseInt(to.slice(1, 3), 16);
  const tg = parseInt(to.slice(3, 5), 16);
  const tb = parseInt(to.slice(5, 7), 16);
  const r = Math.round(fr + (tr - fr) * t);
  const g = Math.round(fg + (tg - fg) * t);
  const b = Math.round(fb + (tb - fb) * t);
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}

function colorForRatio(ratio: number): string {
  if (ratio > 0.5) {
    // 1.0 → 0.5 maps to green → amber
    return lerpHex(HEALTHY_HEX, WARNING_HEX, (1 - ratio) * 2);
  }
  // 0.5 → 0 maps to amber → red
  return lerpHex(WARNING_HEX, CRITICAL_HEX, (0.5 - ratio) * 2);
}

export function attachIntegrityMeter(
  tracker: AtmosphereTracker,
  host: HTMLElement,
): IntegrityMeter {
  const element = document.createElement("div");
  element.dataset["testid"] = "integrity-meter";
  // Mobile-first: tighter margin/width at <640px, expanded at sm:+.
  element.className =
    "pointer-events-none absolute left-0 top-0 m-3 flex flex-col gap-1 font-display text-cosmic-star sm:m-6";

  const labelRow = document.createElement("div");
  labelRow.className = "flex items-baseline justify-between gap-2 text-[10px] tracking-widest text-slate-300/80 sm:gap-3 sm:text-xs";

  const title = document.createElement("span");
  title.textContent = t("integrity.label");

  const percent = document.createElement("span");
  percent.dataset["testid"] = "integrity-percent";
  percent.className = "font-bold text-sm text-cosmic-star sm:text-base";
  percent.textContent = "100%";

  labelRow.append(title, percent);

  const barWrap = document.createElement("div");
  // Narrower bar on mobile so it doesn't push past the screen edge.
  barWrap.className = "h-2 w-36 overflow-hidden rounded-full bg-black/55 ring-1 ring-white/15 sm:h-3 sm:w-56";
  const fill = document.createElement("div");
  fill.dataset["testid"] = "integrity-bar";
  fill.className = "h-full transition-all duration-200";
  fill.style.width = "100%";
  fill.style.background = HEALTHY_HEX;
  barWrap.appendChild(fill);

  element.append(labelRow, barWrap);
  host.appendChild(element);

  let lastRendered = 1;

  const render = (current: number, max: number): void => {
    const ratio = Math.max(0, Math.min(1, current / max));
    const pct = Math.round(ratio * 100);
    fill.style.width = `${pct}%`;
    fill.style.background = colorForRatio(ratio);
    percent.textContent = `${pct}%`;
    // Tint the percent text the same color so the number reads danger.
    percent.style.color = colorForRatio(ratio);
    if (ratio < lastRendered - 0.001 && typeof element.animate === "function") {
      element.animate(
        [
          { transform: "translate(-3px, -1px)" },
          { transform: "translate( 3px,  1px)" },
          { transform: "translate(-2px,  2px)" },
          { transform: "translate( 2px, -2px)" },
          { transform: "translate( 0,    0)" },
        ],
        { duration: 280, easing: "ease-out" },
      );
    }
    lastRendered = ratio;
  };

  const unsub = tracker.subscribeIntegrity((s) => render(s.current, s.max));

  const punchLoss = (): void => {
    if (typeof element.animate !== "function") return;
    element.animate(
      [
        { transform: "scale(1)" },
        { transform: "scale(1.08)" },
        { transform: "scale(1)" },
      ],
      { duration: 240, easing: "ease-out" },
    );
  };

  const detach = (): void => {
    unsub();
    element.remove();
  };

  return { element, punchLoss, detach };
}

export const __testing = { HEALTHY_HEX, WARNING_HEX, CRITICAL_HEX, colorForRatio };
